import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { ArrowLeft, User, Mail, Lock, Camera, Sparkles } from 'lucide-react';

const afterAuthPath = () => { const inv = localStorage.getItem('rbr_pending_invite'); return inv && inv.includes(':') ? `/journey-invite/${inv.split(':')[1]}` : '/profile'; };

const AVATAR_OPTIONS = [
  'https://api.dicebear.com/7.x/avataaars/svg?seed=Felix',
  'https://api.dicebear.com/7.x/avataaars/svg?seed=Aneka',
  'https://api.dicebear.com/7.x/avataaars/svg?seed=Luna',
  'https://api.dicebear.com/7.x/avataaars/svg?seed=Bailey',
  'https://api.dicebear.com/7.x/avataaars/svg?seed=Jasper',
  'https://api.dicebear.com/7.x/avataaars/svg?seed=Willow',
  'https://api.dicebear.com/7.x/avataaars/svg?seed=River',
  'https://api.dicebear.com/7.x/avataaars/svg?seed=Sage',
];

const AuthPage = () => {
  const [mode, setMode] = useState('signup');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [selectedAvatar, setSelectedAvatar] = useState(AVATAR_OPTIONS[0]);
  const [showAvatarPicker, setShowAvatarPicker] = useState(false);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();
  const { signup, login } = useAuth();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!email.trim()) {
      setError('Please enter your email');
      return;
    }
    if (!password || password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    setSubmitting(true);
    try {
      if (mode === 'signup') {
        if (!name.trim()) {
          setError('Please enter your name');
          setSubmitting(false);
          return;
        }
        const result = await signup(name.trim(), email.trim(), password, selectedAvatar);
        if (result.success) {
          navigate(afterAuthPath());
        } else {
          setError(result.error || 'Signup failed');
        }
      } else {
        const result = await login(email.trim(), password);
        if (result.success) {
          navigate(afterAuthPath());
        } else {
          setError(result.error || 'Login failed');
        }
      }
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls =
    'w-full pl-11 pr-4 py-3.5 bg-[#FFFCF3] border border-[#D6AD60]/20 rounded-2xl text-sm text-stone-800 placeholder:text-stone-500 focus:outline-none focus:ring-2 focus:ring-[#D6AD60]/40 shadow-inner';

  return (
    <div className="animate-fade-in min-h-[calc(100vh-152px)]" data-testid="auth-page">
      <div className="px-5 pt-4">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-stone-500 hover:text-[#D6AD60] transition-colors duration-300 mb-6"
          data-testid="auth-back-btn"
        >
          <ArrowLeft className="w-4 h-4" />
          <span className="text-sm">Back</span>
        </button>
      </div>

      <div className="px-5 mb-8 text-center">
        <div className="w-16 h-16 btn-obsidian rounded-3xl flex items-center justify-center mx-auto mb-4 shadow-lg ring-1 ring-[#D6AD60]/40">
          <Sparkles className="w-8 h-8 text-[#AA8347]" />
        </div>
        <h1
          className="text-2xl font-bold text-stone-800 mb-1"
          style={{ fontFamily: "'Playfair Display', serif" }}
        >
          {mode === 'signup' ? 'Create Your Space' : 'Welcome Back'}
        </h1>
        <p className="text-sm text-stone-500">
          {mode === 'signup'
            ? 'Start tracking your reflections journey'
            : 'Continue your mindful journey'}
        </p>
      </div>

      <form onSubmit={handleSubmit} className="px-5">
        {mode === 'signup' && (
          <div className="flex flex-col items-center mb-6">
            <button
              type="button"
              onClick={() => setShowAvatarPicker(!showAvatarPicker)}
              className="relative group"
              data-testid="avatar-picker-toggle"
            >
              <img
                src={selectedAvatar}
                alt="Avatar"
                className="w-20 h-20 rounded-full bg-[#E8DDCB] ring-2 ring-[#D6AD60]/40 shadow-md group-hover:shadow-lg transition-shadow duration-300"
              />
              <div className="absolute -bottom-1 -right-1 w-7 h-7 bg-gradient-to-br from-[#D6AD60] to-[#D6AD60] rounded-full flex items-center justify-center shadow-md">
                <Camera className="w-3.5 h-3.5 text-[#1a1814]" />
              </div>
            </button>
            <span className="text-xs text-stone-500 mt-2">Tap to change avatar</span>

            {showAvatarPicker && (
              <div className="mt-3 bg-[#FFFCF3] border border-[#D6AD60]/20 rounded-2xl p-4 shadow-md w-full animate-scale-in">
                <p className="text-xs text-[#D6AD60] mb-3 font-medium">Choose your avatar</p>
                <div className="grid grid-cols-4 gap-3">
                  {AVATAR_OPTIONS.map((avatar) => (
                    <button
                      key={avatar}
                      type="button"
                      onClick={() => {
                        setSelectedAvatar(avatar);
                        setShowAvatarPicker(false);
                      }}
                      className={`w-full aspect-square rounded-xl overflow-hidden border-2 transition-all duration-300 ${
                        selectedAvatar === avatar
                          ? 'border-[#D6AD60] shadow-md scale-105'
                          : 'border-transparent hover:border-[#D6AD60]/30'
                      }`}
                    >
                      <img src={avatar} alt="Avatar option" className="w-full h-full bg-[#E8DDCB]" />
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {mode === 'signup' && (
          <div className="mb-4">
            <label className="text-xs font-medium text-[#D6AD60] mb-1.5 block">Name</label>
            <div className="relative">
              <User className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-500" />
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Your name"
                className={inputCls}
                data-testid="auth-name-input"
              />
            </div>
          </div>
        )}

        <div className="mb-4">
          <label className="text-xs font-medium text-[#D6AD60] mb-1.5 block">Email</label>
          <div className="relative">
            <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-500" />
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              className={inputCls}
              data-testid="auth-email-input"
            />
          </div>
        </div>

        <div className="mb-4">
          <label className="text-xs font-medium text-[#D6AD60] mb-1.5 block">Password</label>
          <div className="relative">
            <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-500" />
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="At least 6 characters"
              className={inputCls}
              data-testid="auth-password-input"
            />
          </div>
          {mode === 'login' && (
            <div className="text-right mt-2">
              <button
                type="button"
                onClick={() => navigate('/forgot-password')}
                data-testid="auth-forgot-password-link"
                className="text-[11px] text-[#D6AD60] font-semibold hover:text-[#AA8347] transition-colors"
              >
                Forgot password?
              </button>
            </div>
          )}
        </div>

        {error && (
          <div className="mb-4 px-4 py-3 bg-red-500/10 border border-red-500/30 rounded-2xl" data-testid="auth-error">
            <p className="text-xs text-red-400">{error}</p>
          </div>
        )}

        <button
          type="submit"
          disabled={submitting}
          className="w-full py-3.5 btn-gold rounded-2xl text-sm font-semibold active:scale-[0.98] transition-all duration-300 mt-2 disabled:opacity-60"
          data-testid="auth-submit-btn"
        >
          {submitting ? 'Please wait...' : (mode === 'signup' ? 'Create Profile' : 'Log In')}
        </button>

        <div className="text-center mt-5">
          <p className="text-xs text-stone-500">
            {mode === 'signup' ? 'Already have a profile?' : "Don't have a profile?"}{' '}
            <button
              type="button"
              onClick={() => {
                setMode(mode === 'signup' ? 'login' : 'signup');
                setError('');
              }}
              className="text-[#D6AD60] font-semibold hover:text-[#AA8347] transition-colors duration-300"
              data-testid="auth-mode-toggle"
            >
              {mode === 'signup' ? 'Log in' : 'Sign up'}
            </button>
          </p>
        </div>
      </form>
    </div>
  );
};

export default AuthPage;
